import {useCallback, useEffect, useRef} from 'react';
import {BackHandler} from 'react-native';
import {useIsFocused, useNavigation} from '@react-navigation/native';

import {SCREEN_NAMES} from '/enum/tealium.enum';
import {
  getViewTeams,
  resetViewTeamsInfo,
  setIsViewTeam,
  setSelectedTab,
} from '/redux/action/myTeams.action';
import {clearScreenParams} from '/util/async-storage';

import {useClearMyTeamsFilters} from './useClearMyTeamsFilters';
import {useMyTeamsContext} from './useMyTeamsContext';

type TabType = 'my-teams' | 'watchlist';

interface UseMyTeamsNavigationProps {
  routes: any[];
  coachID: string;
  coachIDparams?: string;
  defaultNewCoachesKey: string;
  defaultFrontlineOnly?: boolean;
  handleResetFilters?: (
    tab?: TabType,
    frontlineMyTeams?: boolean,
    frontlineWatchlist?: boolean,
  ) => void;
}

export const useMyTeamsNavigation = ({
  routes,
  coachID,
  coachIDparams,
  defaultNewCoachesKey,
  defaultFrontlineOnly = false,
  handleResetFilters,
}: UseMyTeamsNavigationProps) => {
  const {dispatch, reduxActiveTab} = useMyTeamsContext();

  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const isManualBack = useRef(false);
  const clearMyTeamsFilters = useClearMyTeamsFilters();

  const onBackPress = useCallback(
    (isViewTeam: boolean) => {
      if (isViewTeam) {
        dispatch(
          getViewTeams({
            coachId: coachID,
            pageNumber: 1,
            reportName: 'VIEW_TEAMS',
            appendData: false,
            isDrilldown: false,
          }),
        );
      }

      return false;
    },
    [coachID],
  );

  const handleBackNavigation = useCallback(() => {
    const isGoingBackToRoot = routes.length === 2;
    const isGoingBackToPreviousCoach = routes.length > 2;

    if (routes.length === 1) {
      dispatch(setSelectedTab('my-teams'));
      dispatch(setIsViewTeam(false));

      isManualBack.current = true;
      navigation.goBack();
      return;
    }

    handleResetFilters?.(undefined, defaultFrontlineOnly, false);

    clearMyTeamsFilters(defaultNewCoachesKey);

    dispatch(setSelectedTab('my-teams'));

    let nextIsViewTeam = false;

    if (isGoingBackToRoot) {
      nextIsViewTeam = false;
      dispatch(resetViewTeamsInfo({}));
      clearScreenParams(SCREEN_NAMES.myTeamsReport);
    } else if (isGoingBackToPreviousCoach) {
      nextIsViewTeam = true;
      clearScreenParams(SCREEN_NAMES.myTeamsReport);
    }
    dispatch(setIsViewTeam(nextIsViewTeam));
    isManualBack.current = true;
    navigation.goBack();
    onBackPress(nextIsViewTeam);
  }, [routes.length, coachIDparams, onBackPress]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        handleBackNavigation();
        return true; // prevent default Android back behavior
      },
    );

    const unsubscribe = navigation.addListener('beforeRemove', () => {
      if (isManualBack.current) {
        isManualBack.current = false;
        return;
      }
      dispatch(setSelectedTab('my-teams'));

      if (routes.length === 2) {
        dispatch(resetViewTeamsInfo({}));
        dispatch(setIsViewTeam(false));
        onBackPress(false);
        clearScreenParams(SCREEN_NAMES.myTeamsReport);
      } else if (routes.length > 1) {
        if (reduxActiveTab === 'watchlist') {
          dispatch(setIsViewTeam(false));
          onBackPress(false);
        } else {
          dispatch(setIsViewTeam(true));
          onBackPress(true);
          clearScreenParams(SCREEN_NAMES.myTeamsReport);
        }
      } else if (routes.length === 1) {
        dispatch(setIsViewTeam(false));
        clearScreenParams(SCREEN_NAMES.myTeamsReport);
      }
    });

    return () => {
      backHandler.remove();
      unsubscribe();
    };
  }, [isFocused, navigation, routes.length, reduxActiveTab, onBackPress]);

  return {handleBackNavigation};
};
